import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Calender } from '../../assets/icons/account-management/calender.svg';
import { ReactComponent as UserTag } from '../../assets/icons/account-management/user-tag.svg';
import { ReactComponent as RevenueIcon } from '../../assets/icons/dashboard/revenue-status-icon.svg';
// import { useAppBranding } from '../../config/branding';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import accountApi from '../../utils/api/account';

import FacilityAccountList from './components/facility-account-list';

interface RevenueSummaryData {
  totalRevenue?: number;
  cpoRevenue?: number;
  partnerRevenue?: number;
  totalAdminRevenue?: number;
  totalFoRevenue?: number;
  total_revenue?: number;
  cpo_revenue?: number;
  partner_revenue?: number;
  summary: {
    totalRevenue?: number;
    cpoRevenueShare?: number;
    partnerRevenueShare?: number;
    totalPowerConsumed?: number;
    sessionCount?: number;
  };
}

interface CardData {
  count: string | number;
  title: string;
  subTitle?: string;
  icon: React.ReactNode;
  isLoading: boolean;
  onClick?: () => void;
}

/**
 * Account Management component displaying account metrics and facility account list
 * @returns {React.JSX.Element} Account Management view
 */
function AccountManagement() {
  // const branding = useAppBranding();
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummaryData>({ summary: {} });

  /**
   * Fetches partner revenue summary data
   */
  const fetchRevenueSummaryData = async () => {
    try {
      setIsLoading(true);
      const response = await accountApi.fetchRevenueSummary();
      const data = response?.data?.data ?? response?.data ?? {};
      setRevenueSummary(data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };
  /**
   * Navigates to the partner revenue sharing page
   */
  const handleNavigateToRevenueSharing = () => {
    navigate('/ev-admin/revenue/revenue-sharing/partner');
  };

  const totalRevenue = revenueSummary?.summary?.totalRevenue ?? 0;

  const cpoRevenue = revenueSummary?.summary?.cpoRevenueShare ?? 0;

  const partnerRevenue = revenueSummary?.summary?.partnerRevenueShare ?? 0;

  const summaryCards: CardData[] = [
    {
      title: t('totalRevenue'),
      count: ToFixedValue(totalRevenue),
      // subTitle: branding.text.currency,
      icon: <RevenueIcon />,
      isLoading,
    },
    {
      title: t('cpoRevenue'),
      count: ToFixedValue(cpoRevenue),
      // subTitle: branding.text.currency,
      icon: <Calender className="primary-icon" />,
      isLoading,
      onClick: handleNavigateToRevenueSharing,
    },
    {
      title: t('partnerRevenue'),
      count: ToFixedValue(partnerRevenue),
      // subTitle: branding.text.currency,
      icon: <UserTag className="primary-icon" />,
      isLoading,
      onClick: handleNavigateToRevenueSharing,
    },
  ];

  useEffect(() => {
    fetchRevenueSummaryData();
  }, []);

  /**
   * Renders a single dashboard metric card
   * @param {CardData} card - The card data to render
   * @returns {React.JSX.Element} Card component
   */
  const renderCard = (card: CardData) => (
    <div
      className={`new-dashboard-card ${card.onClick ? 'clickable' : ''}`}
      key={card.title}
      onClick={card.onClick}
      role="presentation">
      <div className="top-section">
        <h1 className="count">
          {card.isLoading ? '...' : card.count}{' '}
          {card.subTitle && <span className="subtitle">{card.subTitle}</span>}
        </h1>
        <div className="icon-wrapper">{card.icon}</div>
      </div>
      <div className="bottom-section">
        <p className="title">{card.title}</p>
      </div>
    </div>
  );

  return (
    <div className="account-management-wrapper">
      <div className="container">
        <div className="header-title">
          <div className="dashboard-cards-outer">
            <div className="dashboard-cards-wrap-new">
              {summaryCards.map((card) => renderCard(card))}
            </div>
          </div>
        </div>
        <div className="body-container">
          <div className="table-content">
            <FacilityAccountList />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountManagement;
